import { useTranslationCapManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useMutation } from '@tanstack/react-query';

import {
	ProgrammeSecurityAllocationTct,
	SecurityIssuedIn,
	UpdateProgrammeSecurityAllocationDto,
} from '@dealstack/shared/lib/generated';
import {
	AllocationFormDto,
	AllocationShape,
} from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Securities/SecurityDetails/SecurityTabs/SecurityAllocationsTabPanel/SecurityAllocationDrawer/allocations.zod.ts';
import {
	createSecurityAllocation,
	updateSecurityAllocation,
} from '@dealstack/frontend/app/services/securityAllocations.service.ts';
import { useParams } from 'react-router-dom';
import { useContext, useEffect } from 'react';
import { SecurityDetailsControllerContext } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Securities/SecurityDetails/useSecurityDetailsController.ts';

export enum SECURITY_ALLOCATION_FORM_FIELD {
	mipId = 'mipId',
	allocation = 'allocation',
}

const DEFAULT_FORM_VALUES = {
	[SECURITY_ALLOCATION_FORM_FIELD.mipId]: null,
	[SECURITY_ALLOCATION_FORM_FIELD.allocation]: '',
} as unknown as AllocationFormDto;

export const useCreateSecurityAllocationDrawerController = (
	onClose: (securityEventId?: string) => void,
	allocation: ProgrammeSecurityAllocationTct | null,
	onSuccess: () => void
) => {
	const tCapManage = useTranslationCapManage();
	const { securityId } = useParams();

	const { getAllocationsRequest, getSecurityDetailsRequest } = useContext(
		SecurityDetailsControllerContext
	);

	const securityIssuedInCurrency =
		getSecurityDetailsRequest?.data?.issuedIn === SecurityIssuedIn.CURRENCY;

	const createAllocationRequest = useMutation({
		mutationFn: createSecurityAllocation,
	});
	const updateAllocationRequest = useMutation({
		mutationFn: updateSecurityAllocation,
	});

	const allocationForm = useForm<AllocationFormDto>({
		mode: 'all',
		resolver: zodResolver(AllocationShape),
		defaultValues: DEFAULT_FORM_VALUES,
		shouldFocusError: false,
	});

	useEffect(() => {
		if (allocation) {
			allocationForm.reset({
				[SECURITY_ALLOCATION_FORM_FIELD.mipId]: allocation?.mipId,
				[SECURITY_ALLOCATION_FORM_FIELD.allocation]: allocation?.allocation,
			});
		} else {
			allocationForm.reset(DEFAULT_FORM_VALUES);
		}
	}, [allocationForm, allocation]);

	const onSubmit = () => (formValues: AllocationFormDto) => {
		if (!securityId) {
			return;
		}

		const payload: UpdateProgrammeSecurityAllocationDto = {
			mipId: formValues[SECURITY_ALLOCATION_FORM_FIELD.mipId],
			allocation: formValues[SECURITY_ALLOCATION_FORM_FIELD.allocation],
			securityId,
		};

		if (allocation) {
			updateAllocationRequest.mutate(
				{
					allocationId: allocation?.id ?? '',
					body: payload,
				},
				{
					onSuccess: async () => {
						onSuccess?.();
						onClose();
						void getAllocationsRequest.refetch();
						showToastV2({
							message: tCapManage('securities.details.allocations.update.success'),
						});
					},
				}
			);
		} else {
			createAllocationRequest.mutate(payload, {
				onSuccess: async () => {
					onSuccess?.();
					onClose();
					void getAllocationsRequest.refetch();
					showToastV2({
						message: tCapManage('securities.details.allocations.create.success'),
					});
				},
			});
		}
	};

	return {
		onSubmit,
		allocationForm,
		isLoading: createAllocationRequest.isPending || updateAllocationRequest.isPending,
		securityIssuedInCurrency,
		securityCurrency: getSecurityDetailsRequest?.data?.currency,
	};
};
